import TimelineEvents from "@/machines/components/common/timeline-editor/main-preview/timeline-editor";
import { useGetTruckEvents, useBatchEditTruckEvents, ROLLING_WINDOW_OPTIONS } from "@/machines/api/queries/events.queries.ts";
import { useTimelineEvents } from "@/machines/api/queries/use-timeline-events";
import { useGetAlerts } from "@/alerts/api/queries/machine-alerts.queries.ts";

/**
 * Backstop cadence for the timeline's alert markers. Arrivals come from the live
 * channel (which invalidates every alert list), so this only has to cover the
 * case where that channel is down.
 */
const ALERT_TRACK_POLL_MS = 60_000;
import { Machine } from "@/machines/types/machine.ts";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { LoadingProgress } from "./loading-progress";
import { Alert, AlertStatus } from "@/alerts/types/machine-alert";
import type { MachineEvent } from "@/machines/types/event";
import { useTimelineTrackSettingsStore } from "@/shared/stores/useTimelineTrackSettingsStore";
import { useModal } from "@/shared/lib/modal/modal-context";
import { Button } from "@/ui/components/ui/button";
import { Settings, Edit3, Save, X, RotateCcw, Trash2, Split } from "lucide-react";
import { useTimelineContext } from "@/machines/context/timeline-context";
import { useTimelineEditQueueStore } from "@/machines/stores/useTimelineEditQueueStore";
import { getEventUniqueId } from "@/machines/components/common/timeline-editor/utils/timelineHelpers";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";

interface TimelineSegment {
  start: number;
  end: number;
  hasVideo: boolean;
}

export interface AvailableTrack {
  key: string;
  label: string;
  defaultHeight: number;
}

interface MainPreviewProps {
  device_mxid: Machine["device_mxid"];
  videoSegments?: TimelineSegment[];
  /** Window stretches never probed for footage — drawn as "not asked yet". */
  videoPendingRanges?: { from: number; to: number }[];
  onEventDoubleClick?: (event: any) => void;
  /**
   * Non-edit-mode click on a segment — opens the event's detail (e.g. the
   * map's on-map card). See TimelineEvents.onEventOpen.
   */
  onEventOpen?: (event: MachineEvent, isTruckCycle: boolean) => void;
  /**
   * Compact mode (used by the `/map` panel): hide the persistent Edit Mode /
   * Track Settings buttons (the host renders them as header icons); the bottom
   * bar only appears in edit mode for Save/Discard/Split/Delete.
   */
  compactToolbar?: boolean;
  /** Reports the track list so a host can open the track-settings modal. */
  onAvailableTracks?: (tracks: AvailableTrack[]) => void;
  /**
   * Poll the event/alert queries at this interval (ms) so the timeline stays
   * live; freshly arrived events pop in with an enter animation. Omit for the
   * regular fetch-once behavior.
   */
  refetchIntervalMs?: number;
  /**
   * Fetch this range instead of the global date-time-range store — used by the
   * map's realtime mode with a rolling window. `rolling` marks the window as
   * short-lived: previous data stays visible while the shifted window loads,
   * and its cache entries are garbage-collected quickly.
   */
  timeWindow?: { fromDate: Date; toDate: Date; rolling?: boolean };
  /**
   * Live view: auto-scroll the timeline to a rolling window of this many ms
   * anchored to the wall clock (see TimelineEvents.liveViewSpanMs).
   */
  liveViewSpanMs?: number;
  /**
   * Live view: fraction (0–1) of the visible span kept empty ahead of the live
   * edge, so `now` sits at `(1 - liveViewLeadFraction)` of the width (see
   * TimelineEvents.liveViewLeadFraction). Defaults to `0`.
   */
  liveViewLeadFraction?: number;
  /**
   * Map live-follow: anchor the live domain on the CURSOR at this fraction (0–1)
   * of the width instead of on `now`, pinning the video's live position (e.g. 0.75
   * → 75%) with the newest video-less events to its right. See
   * TimelineEvents.liveCursorAnchor.
   */
  liveCursorAnchor?: number;
  /**
   * Live domain computed once for a whole STACK of timelines; overrides this
   * row's own. See TimelineEvents.liveDomainOverride.
   */
  liveDomainOverride?: { min: number; max: number } | null;
  /**
   * The instant to draw the playback cursor at, so stacked rows share one
   * playhead. See TimelineEvents.sharedPlaybackMs.
   */
  sharedPlaybackMs?: number | null;
  /**
   * Live-view ALERT-REVIEW override (ms): freeze the domain to a fixed window
   * centered on this moment (a selected alert/event) instead of the rolling live
   * window, so it stays visible even when older than the live span. Null → rolling
   * live. See TimelineEvents.reviewTime.
   */
  reviewTime?: number | null;
  /**
   * Skip merging the (global) edit-queue changes and created events into the
   * rendered tracks. Used by the map's inactive stacked timelines so another
   * machine's pending edits/splits don't leak onto their tracks — only the
   * active, editable timeline shows queued edits.
   */
  suppressPendingEdits?: boolean;
  /**
   * Render the timeline's bottom time axis / tick labels. Defaults to `true`.
   * The map's stacked live timelines pass `false` on every row except the last
   * so a single shared axis renders once beneath the stack.
   */
  showTimeAxis?: boolean;
}

const MainPreview = ({
  device_mxid,
  videoSegments = [],
  videoPendingRanges = [],
  onEventDoubleClick,
  onEventOpen,
  compactToolbar = false,
  onAvailableTracks,
  refetchIntervalMs,
  timeWindow,
  liveViewSpanMs,
  liveViewLeadFraction,
  liveCursorAnchor,
  liveDomainOverride = null,
  sharedPlaybackMs = null,
  reviewTime,
  suppressPendingEdits = false,
  showTimeAxis = true,
}: MainPreviewProps) => {
  const { t } = useTranslation("machines");
  const { value } = useDateTimeRangeStore();
  const fromDate = timeWindow?.fromDate ?? value.fromDate;
  const toDate = timeWindow?.toDate ?? value.toDate;
  const { trackSettings } = useTimelineTrackSettingsStore();
  const { openModal } = useModal();
  const { isEditMode, setIsEditMode, selectedEventForEdit, setSelectedEventForEdit, createStartTime, setCreateStartTime } = useTimelineContext();
  const { getChangesCount, clearChanges, getChangesForApi, hasPendingChanges, deleteEvent, splitEvent, getCreatedEvents, changes, applyLocalChangesToEvent, createEvent } = useTimelineEditQueueStore();
  const batchEditMutation = useBatchEditTruckEvents();

  const pollOptions: { refetchInterval: number | false; gcTime?: number; keepPrevious?: boolean } = {
    refetchInterval: refetchIntervalMs ?? false,
    ...(timeWindow?.rolling && ROLLING_WINDOW_OPTIONS),
  };

  // Live mode may take the tail-and-buffer path (runtime flag, see
  // `useTimelineEvents`); range mode is historical review and always fetches the
  // window once. Machine-detail passes no `timeWindow`, so it is untouched.
  const { data: events, status: statusEvents, pendingRanges, isFetching: eventsFetching } = useTimelineEvents(
    { start_time: fromDate, end_time: toDate, device_mxid, page: 1, itemsPerPage: 20000 },
    { ...pollOptions, live: !!timeWindow?.rolling },
  );

  const { data: truckEvents, status: statusTruckEvents } = useGetTruckEvents({
    start_time: fromDate,
    end_time: toDate,
    device_mxid,
    page: 1,
    itemsPerPage: 1000,
  }, { ...pollOptions, fetchAll: true });

  // 1000, matching the truck-events cap above: this track draws one machine's
  // alerts as instant markers over a single window, and the response is ordered
  // newest-first, so a cap can only drop the OLDEST markers of a window that is
  // already far past legibility. (Was 1_000_000 — an unbounded page fetched once
  // per timeline row, i.e. once per machine on `/map`.)
  // Polled far slower than the events beside it, and deliberately so.
  //
  // A new alert reaches this track by PUSH: the Firestore hint invalidates every
  // alert list (`isAlertListQueryKey` matches this key), so the markers appear
  // when the alert does, not when a timer next fires. At the row's 5s cadence
  // this query was simply asking again for what it had — 11 calls a minute for
  // one machine, on top of the map feed already fetching the same alerts under a
  // different param spelling. The interval is now only a backstop for a dead
  // channel, which the channel's own jittered fallback also covers.
  const alertPollOptions = pollOptions.refetchInterval
    ? { ...pollOptions, refetchInterval: ALERT_TRACK_POLL_MS }
    : pollOptions;
  const { data: alerts, status: statusAlerts } = useGetAlerts({
    start_time: fromDate,
    end_time: toDate,
    device_mxid,
    page: 1,
    itemsPerPage: 1000,
  }, alertPollOptions);

  // Track which event/alert IDs have already been rendered so segments arriving
  // in a later poll can be flagged `isNew` (drives the pop-in animation). The
  // initial load is seeded silently — nothing animates on first paint.
  const seenEventIdsRef = useRef<Set<string> | null>(null);

  const collectCurrentIds = useCallback(() => {
    const ids = new Set<string>();
    (events?.data ?? []).forEach((e) => ids.add(String(getEventUniqueId(e))));
    (truckEvents?.data ?? []).forEach((e) => ids.add(String(getEventUniqueId(e))));
    (alerts?.data ?? []).forEach((a: Alert) => ids.add(String(a.id)));
    ids.delete("");
    return ids;
  }, [events?.data, truckEvents?.data, alerts?.data]);

  const newEventIds = useMemo(() => {
    const seen = seenEventIdsRef.current;
    if (seen === null) return new Set<string>();
    const fresh = new Set<string>();
    collectCurrentIds().forEach((id) => {
      if (!seen.has(id)) fresh.add(id);
    });
    return fresh;
  }, [collectCurrentIds]);

  useEffect(() => {
    // Reset the "seen" registry when switching machine or date range so the
    // next initial load doesn't animate everything.
    seenEventIdsRef.current = null;
  }, [device_mxid, fromDate, toDate]);

  useEffect(() => {
    if (seenEventIdsRef.current === null) {
      // Seed only once all queries settled so a slower query's initial payload
      // isn't flagged as "new" and animated on first paint.
      if (statusEvents !== "success" || statusTruckEvents !== "success" || statusAlerts !== "success") return;
      seenEventIdsRef.current = collectCurrentIds();
    } else {
      collectCurrentIds().forEach((id) => seenEventIdsRef.current!.add(id));
    }
  }, [collectCurrentIds, statusEvents, statusTruckEvents, statusAlerts]);

  // Track heights based on track type
  const TRACK_HEIGHTS: Record<string, number> = {
    // Events Timeline
    "cycle-state-unknown": 25,
    dig: 25,
    dump: 25,
    swing: 25,
    abnormal: 25,
    unproductive: 25,

    // Truck detections timeline
    "truck-state-unknown": 25,
    "truck-loaded": 25,
    "truck-empty": 25,

    // Truck Events timeline
    "truck_cycles": 25,
    "truck-cycle": 25,
    "time-between-trucks": 25,

    // Fragmentation values (truck events)
    good: 25,
    excellent: 25,
    medium: 25,
    poor: 25,
    bad: 25,

    // Alerts timeline
    alerts: 10,
    "alert-pending": 30,
    "alert-resolved": 30,
    "alert-ignored": 30,

    default: 25,
  };

  // Convert any color format to RGBA with opacity (like video segments)
  const convertToRgba = useCallback((color: string, opacity: number = 0.3): string => {
    // If already rgba, just return it
    if (color.startsWith('rgba')) return color;

    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Handle rgb colors
    if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        return `rgba(${matches[0]}, ${matches[1]}, ${matches[2]}, ${opacity})`;
      }
    }

    // Handle hsl colors - convert to rgb first
    if (color.startsWith('hsl')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const h = parseInt(matches[0]) / 360;
        const s = parseInt(matches[1]) / 100;
        const l = parseInt(matches[2]) / 100;

        let r, g, b;
        if (s === 0) {
          r = g = b = l;
        } else {
          const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
          };

          const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
          const p = 2 * l - q;
          r = hue2rgb(p, q, h + 1/3);
          g = hue2rgb(p, q, h);
          b = hue2rgb(p, q, h - 1/3);
        }

        return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${opacity})`;
      }
    }

    // Fallback: return color as is
    return color;
  }, []);


  // Generate available tracks metadata for settings panel
  const availableTracks = useMemo(() => {
    const trackKeys = new Set<string>();

    // Collect all unique track keys from events
    const allEvents = [
      ...(events?.data ?? []),
      ...(truckEvents?.data ?? []),
    ];

    allEvents.forEach(event => {
      let key: string = "default";
      if ('eventLayer' in event && event.eventLayer) {
        key = String(event.eventLayer).toLowerCase();
      } else if ('state' in event && event.state) {
        key = String(event.state).toLowerCase();
      } else if ('type' in event && event.type) {
        key = String(event.type).toLowerCase();
      }
      trackKeys.add(key);
    });

    // Add alerts track if alerts exist
    if (alerts?.data && alerts.data.length > 0) {
      trackKeys.add("alerts");
    }

    return Array.from(trackKeys).map(key => ({
      key,
      label: key,
      defaultHeight: TRACK_HEIGHTS[key] || TRACK_HEIGHTS.default,
    }));
  }, [events?.data, truckEvents?.data, alerts?.data]);

  // Surface the track list so a compact host (e.g. the /map panel) can open the
  // track-settings modal from its own header. `availableTracks` is a fresh array
  // on every poll (its query data reference changes), but its identity is fully
  // captured by the set of track keys — report only when THAT changes, so a 5s
  // poll with an unchanged track set doesn't push a new array up and re-render
  // the host tree. Each track's label/height derive purely from its key.
  const lastReportedTrackKeysRef = useRef<string | null>(null);
  useEffect(() => {
    const sig = availableTracks
      .map((t) => t.key)
      .sort()
      .join("|");
    if (sig === lastReportedTrackKeysRef.current) return;
    lastReportedTrackKeysRef.current = sig;
    onAvailableTracks?.(availableTracks);
  }, [availableTracks, onAvailableTracks]);

  const tracks = useMemo(() => {
    const groupedTracks: Record<string, any[]> = {};

    // Process alerts as instant events on a single track
    const allAlerts = alerts?.data ?? [];

    const getAlertColor = (status: AlertStatus): string => {
      switch (status) {
        case AlertStatus.Pending:
          return convertToRgba("#FFA500", 0.7); // Orange
        case AlertStatus.Resolved:
          return convertToRgba("#00FF00", 0.7); // Green
        case AlertStatus.Ignored:
          return convertToRgba("#808080", 0.7); // Gray
        default:
          return convertToRgba("#FFFF00", 0.7); // Yellow
      }
    };

    if (allAlerts.length > 0) {
      const alertsKey = "alerts";
      if (!groupedTracks[alertsKey]) groupedTracks[alertsKey] = [];

      const alertSettings = trackSettings[alertsKey] || { visible: true, height: 10 };
      const alertTrackHeight = alertSettings.height || 10;

      allAlerts.forEach((alert: Alert) => {
        const timestamp = new Date(alert.timestamp).getTime();
        const duration = 5000; // 5 seconds duration for visibility

        groupedTracks[alertsKey].push({
          start: timestamp,
          end: timestamp + duration,
          color: getAlertColor(alert.status),
          event: alert,
          trackKey: alertsKey,
          trackHeight: alertTrackHeight,
          isAlert: true, // Flag to identify alerts
          hidden: alertSettings.visible === false, // Add hidden flag
          isNew: newEventIds.has(String(alert.id)),
        });
      });
    }

    // Process all events dynamically - no filtering, no hardcoded types
    // Apply local changes to events (merge API data with edits)
    const rawEvents = [
      ...(events?.data ?? []),
      ...(truckEvents?.data ?? []),
    ];

    // Merge API events with local changes (skipped for read-only stacked rows —
    // the global edit queue belongs to another, active machine).
    const allEvents = suppressPendingEdits
      ? rawEvents
      : rawEvents
          .map(event => applyLocalChangesToEvent(event))
          .filter(event => event !== null); // Remove deleted events

    allEvents.forEach(event => {
      // Determine key for grouping (from eventLayer or state)
      let key: string = "default";

      if ('eventLayer' in event && event.eventLayer) {
        key = String(event.eventLayer).toLowerCase();
      } else if ('state' in event && event.state) {
        key = String(event.state).toLowerCase();
      } else if ('type' in event && event.type) {
        key = String(event.type).toLowerCase();
      } else if ('category' in event && event.category) {
        key = String(event.category).toLowerCase();
      } else if ('name' in event && event.name) {
        key = String(event.name).toLowerCase();
      }

      const backendColor = event.color || '#3b82f6'; // Fallback to blue if no color
      const color = convertToRgba(backendColor, 0.3);

      if (!groupedTracks[key]) groupedTracks[key] = [];

      const defaultHeight = TRACK_HEIGHTS[key] || TRACK_HEIGHTS.default;
      const settings = trackSettings[key] || { visible: true, height: defaultHeight };
      const trackHeight = settings.height || defaultHeight;

      // Convert times to timestamps
      const startTimestamp = new Date(event.startTime).getTime();
      const endTimestamp = new Date(event.endTime).getTime();

      // Debug: log if this event has modified times
      const eventId = event.eventId || event.id;

      groupedTracks[key].push({
        start: startTimestamp,
        end: endTimestamp,
        color,
        event,
        trackKey: key,
        trackHeight,
        hidden: settings.visible === false, // Add hidden flag
        isNew: newEventIds.has(String(getEventUniqueId(event))),
      });
    });

    // Add created events from queue AFTER regular events (from split operations or manual creates).
    // Read-only stacked rows skip this — the queue belongs to another, active machine.
    const createdEvents = suppressPendingEdits ? [] : getCreatedEvents();

    // Helper function to recursively find the original parent event
    const findOriginalParent = (parentId: string | undefined): any => {
      if (!parentId) return null;

      // Try to find in allEvents (API events) first
      const apiParent = allEvents.find(e => {
        if ('truckId' in e && e.truckId === parentId) return true;
        if (e.id === parentId) return true;
        return false;
      });

      if (apiParent) {
        return apiParent; // Found the original API event
      }

      // Not in API events, must be a created event - search in createdEvents
      const createdParent = createdEvents.find(c => c.id === parentId);
      if (createdParent) {
        // Recursively find the original parent of this created event
        return findOriginalParent(createdParent.parentId);
      }

      return null; // Parent not found
    };

    createdEvents.forEach(createdChange => {
      // Create a synthetic event object for rendering
      // Use parent event's properties if available
      // For TruckEvents, parentId is truckId; for MachineEvents, parentId is id

      // Recursively find the original parent event (handles multiple nested splits)
      const parentEvent = findOriginalParent(createdChange.parentId);

      // Determine track key from parent event (same logic as regular events)
      let key: string = "default";
      if (parentEvent) {
        if ('eventLayer' in parentEvent && parentEvent.eventLayer) {
          key = String(parentEvent.eventLayer).toLowerCase();
        } else if ('state' in parentEvent && parentEvent.state) {
          key = String(parentEvent.state).toLowerCase();
        } else if ('type' in parentEvent && parentEvent.type) {
          key = String(parentEvent.type).toLowerCase();
        } else if ('category' in parentEvent && parentEvent.category) {
          key = String(parentEvent.category).toLowerCase();
        } else if ('name' in parentEvent && parentEvent.name) {
          key = String(parentEvent.name).toLowerCase();
        }
      }

      const backendColor = parentEvent?.color || '#10b981'; // Green for new events
      const color = convertToRgba(backendColor, 0.3);

      if (!groupedTracks[key]) groupedTracks[key] = [];

      const defaultHeight = TRACK_HEIGHTS[key] || TRACK_HEIGHTS.default;
      const settings = trackSettings[key] || { visible: true, height: defaultHeight };
      const trackHeight = settings.height || defaultHeight;

      // createdChange times are stored as ISO strings, convert to timestamps
      const startTimestamp = new Date(createdChange.startTime).getTime();
      const endTimestamp = new Date(createdChange.endTime).getTime();

      // Build created event - copy parent properties but override with new values
      const createdEvent: any = {
        ...(parentEvent || {}), // Copy parent properties if available
        id: createdChange.id, // Override with new ID
        startTime: new Date(startTimestamp),
        endTime: new Date(endTimestamp),
      };

      // For TruckEvents, remove the parent's eventId and truckId since this is a new event
      // The new event uses 'id' field, not 'eventId'
      if ('truckId' in createdEvent) {
        delete createdEvent.eventId;
        delete createdEvent.truckId;
      }

      groupedTracks[key].push({
        start: startTimestamp,
        end: endTimestamp,
        color,
        event: createdEvent,
        trackKey: key,
        trackHeight,
        hidden: settings.visible === false,
      });
    });

    // Sort each track by start time to maintain chronological order
    Object.keys(groupedTracks).forEach(key => {
      groupedTracks[key].sort((a, b) => a.start - b.start);
    });

    // Return all tracks (including hidden ones)
    // The renderer will handle hiding them
    return Object.values(groupedTracks);
  }, [events?.data, truckEvents?.data, alerts?.data, trackSettings, changes, convertToRgba, applyLocalChangesToEvent, getCreatedEvents, newEventIds, suppressPendingEdits]);

  const isLoading = statusEvents === "pending" || statusTruckEvents === "pending" || statusAlerts === "pending";

  // Simulate progress for better UX
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    } else {
      setProgress(100);
    }
  }, [isLoading]);

  const handleOpenTrackSettings = () => {
    openModal("timeline-track-settings", { availableTracks });
  };

  const visibleCount = availableTracks.filter(
    (track) => trackSettings[track.key]?.visible !== false
  ).length;

  const handleToggleEditMode = () => {
    if (isEditMode) {
      // Exiting edit mode - clear selection
      setSelectedEventForEdit(null);
    }
    setIsEditMode(!isEditMode);
  };

  const handleSaveChanges = async () => {
    const changes = getChangesForApi();

    if (changes.length === 0) {
      console.log("No changes to save");
      toast.error(t("timeline.toast.noChanges"));
      return;
    }

    // Store the request payload for error debugging
    const requestPayload = {
      device_mxid,
      commands: changes
    };

    await toast.promise(
      batchEditMutation.mutateAsync(requestPayload),
      {
        loading: t("timeline.toast.saving", { count: changes.length }),
        success: (result) => {
          console.log("Changes saved successfully:", result);
          // Clear the queue and exit edit mode
          clearChanges();
          setIsEditMode(false);
          setSelectedEventForEdit(null);
          return t("timeline.toast.saved", { count: changes.length });
        },
        error: (error: any) => {
          console.error("Failed to save changes:", error);
          // Open error modal with full error details, request payload, and cache debug info
          openModal("error-detail", {
            title: "Failed to Save Changes",
            error: error?.response?.data,
            requestPayload: requestPayload,
            cacheDebugInfo: error?._cacheDebugInfo,
          });
          return t("timeline.toast.saveFailed");
        },
      }
    );
  };

  const handleDiscardChanges = () => {
    clearChanges();
    setIsEditMode(false);
    setSelectedEventForEdit(null);
  };

  const handleDeleteSelectedEvent = () => {
    if (!selectedEventForEdit) return;
    // Use truckId for truck events, id for machine events (same logic as split)
    const uniqueId = ('truckId' in selectedEventForEdit && selectedEventForEdit.truckId)
      ? selectedEventForEdit.truckId
      : selectedEventForEdit.id;
    deleteEvent(uniqueId);
    setSelectedEventForEdit(null);
  };

  const handleSplitSelectedEvent = () => {
    if (!selectedEventForEdit) return;

    // Get the CURRENT times of the selected event (including any pending modifications)
    const currentStart = selectedEventForEdit.startTime;
    const currentEnd = selectedEventForEdit.endTime;

    // Use truckId for truck events, id for machine events
    const uniqueId = ('truckId' in selectedEventForEdit && selectedEventForEdit.truckId)
      ? selectedEventForEdit.truckId
      : selectedEventForEdit.id;

    // Use the store's splitEvent function which handles:
    // - Calculating midpoint from CURRENT segment times (with modifications)
    // - Snapping the midpoint to nearest DIG-layer event boundary
    // - Ensuring first half ends exactly where second half starts (no gap/overlap)
    // - Creating both UPDATE (first half) and CREATE (second half) changes
    splitEvent(uniqueId, currentStart, currentEnd, events?.data);

    setSelectedEventForEdit(null);
  };

  // Keyboard shortcuts in edit mode
  useEffect(() => {
    if (!isEditMode || !selectedEventForEdit) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete key - delete selected event
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteSelectedEvent();
      }

      // S key - split selected event
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        handleSplitSelectedEvent();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, selectedEventForEdit]);

  return (
    <div id="main-preview" className="h-full flex flex-col gap-2">
      {/* Timeline */}
      <div className="flex-1">
        {isLoading ? (
          <LoadingProgress isLoading={isLoading} progress={Math.round(progress)} />
        ) : (
          <TimelineEvents
            tracks={tracks}
            videoSegments={videoSegments}
            videoPendingRanges={videoPendingRanges}
            pendingRanges={pendingRanges}
            // Refreshing over data we already hold. Distinct from the first
            // load, which has its own empty state — here there ARE segments on
            // screen and the question is whether to trust them as current.
            isRefreshing={eventsFetching && (events?.data?.length ?? 0) > 0}
            // The FIRST load, or a refetch that has briefly emptied the list.
            // Distinct from `isRefreshing` above: there are no segments on
            // screen, so the tracks would otherwise collapse to nothing and read
            // as "this machine has no data" rather than "not here yet".
            isLoading={eventsFetching && (events?.data?.length ?? 0) === 0}
            onEventDoubleClick={onEventDoubleClick}
            onEventOpen={onEventOpen}
            liveViewSpanMs={liveViewSpanMs}
            liveViewLeadFraction={liveViewLeadFraction}
            liveCursorAnchor={liveCursorAnchor}
            liveDomainOverride={liveDomainOverride}
            sharedPlaybackMs={sharedPlaybackMs}
            reviewTime={reviewTime}
            showTimeAxis={showTimeAxis}
          />
        )}
      </div>

      {/* Bottom Controls */}
      {!isLoading && availableTracks.length > 0 && (!compactToolbar || isEditMode) && (
        <div className="flex justify-between items-center gap-2">
          {/* Left Side - Edit Mode & Actions */}
          <div className="flex items-center gap-2">
            {!compactToolbar && (
              <Button
                variant={isEditMode ? "default" : "outline"}
                size="sm"
                className="gap-2"
                onClick={handleToggleEditMode}
                disabled={hasPendingChanges()}
              >
                {isEditMode ? (
                  <>
                    <X className="h-4 w-4" />
                    {t("timeline.exitEditMode")}
                  </>
                ) : (
                  <>
                    <Edit3 className="h-4 w-4" />
                    {t("timeline.editMode")}
                  </>
                )}
              </Button>
            )}

            {/* Save/Discard buttons - show when there are changes */}
            {isEditMode && hasPendingChanges() && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  className="gap-2"
                  onClick={handleSaveChanges}
                  disabled={batchEditMutation.isPending || getChangesCount() === 0}
                >
                  <Save className="h-4 w-4" />
                  {batchEditMutation.isPending
                    ? t("settingsForm.actions.saving")
                    : t("timeline.saveChanges", { value: getChangesCount() })}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={handleDiscardChanges}
                >
                  <RotateCcw className="h-4 w-4" />
                  {t("timeline.discard")}
                </Button>
              </>
            )}

            {/* Event Action Buttons - show when event is selected */}
            {isEditMode && selectedEventForEdit && (
              <>
                <div className="h-6 w-px bg-border" /> {/* Separator */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={handleSplitSelectedEvent}
                >
                  <Split className="h-4 w-4" />
                  {t("timeline.split")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 text-destructive hover:text-destructive"
                  onClick={handleDeleteSelectedEvent}
                >
                  <Trash2 className="h-4 w-4" />
                  {t("timeline.delete")}
                </Button>

                <div className="h-6 w-px bg-border" />
                <span className="text-xs text-muted-foreground">
                  <Trans
                    t={t}
                    i18nKey="timeline.previewVideoHint"
                    components={{
                      key: <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono" />,
                    }}
                  />
                </span>
              </>
            )}

            {isEditMode && !selectedEventForEdit && !hasPendingChanges() && (
              <span className="text-xs text-muted-foreground">
                {createStartTime
                  ? t("timeline.hintSetEndTime")
                  : t("timeline.hintSelectEvent")}
              </span>
            )}

          </div>

          {/* Track Settings Button */}
          {!compactToolbar && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleOpenTrackSettings}
              disabled={isEditMode}
            >
              <Settings className="h-4 w-4" />
              {t("timeline.trackSettings")}
              <span className="text-xs text-muted-foreground">
                {t("timeline.trackCount", { visible: visibleCount, total: availableTracks.length })}
              </span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MainPreview;
